import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { productService } from '../../services/productService'
import { ProductStatus } from '../../types/product'
import { usePermissions } from '../../hooks/usePermissions'

interface ProductExportDialogProps {
  open: boolean
  onClose: () => void
}

const ProductExportDialog: React.FC<ProductExportDialogProps> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const canExport = hasPermission('products.bulk_export')
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv')
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (!canExport) {
      enqueueSnackbar('You do not have permission to export products', { variant: 'error' })
      return
    }
    setIsExporting(true)
    try {
      const blob = await productService.bulkExport({
        status: statusFilter || undefined,
        category_id: categoryFilter || undefined,
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      enqueueSnackbar('Products exported successfully', { variant: 'success' })
      onClose()
    } catch (error: any) {
      console.error('Export error:', error)
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to export products',
        { variant: 'error' }
      )
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Products</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {!canExport && (
            <Alert severity="error">
              You do not have permission to export products.
            </Alert>
          )}
          <Alert severity="info">
            Export products with all pricing tiers (MRP, sale price, wholesale price, purchase price)
            and specifications.
          </Alert>

          <FormControl component="fieldset">
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup
              value={format}
              onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx')}
            >
              <FormControlLabel
                value="csv"
                control={<Radio />}
                label="CSV (Comma-separated values)"
              />
              <FormControlLabel
                value="xlsx"
                control={<Radio />}
                label="XLSX (Excel format)"
                disabled
              />
            </RadioGroup>
            {format === 'xlsx' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Excel format coming soon. Please use CSV for now.
              </Typography>
            )}
          </FormControl>

          <TextField
            select
            fullWidth
            label="Filter by Status (Optional)"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ProductStatus | '')}
            helperText="Leave empty to export all products"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
            <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
            <MenuItem value="DISCONTINUED">Discontinued</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Filter by Category ID (Optional)"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            helperText="Enter category UUID to filter by category"
            placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={isExporting || !canExport}
        >
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductExportDialog
